import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { questsAPI } from '@/api/quests';
import { getAuthToken, getPortalType, isAuthenticated } from '@/utils/auth';

const QuestsContext = createContext(null);

export const useQuests = () => {
  const context = useContext(QuestsContext);
  if (!context) {
    throw new Error('useQuests must be used within a QuestsProvider');
  }
  return context;
};

export const QuestsProvider = ({ children }) => {
  const [quests, setQuests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    status: null, // 'draft', 'in_progress', 'completed'
    mode: null,   // 'practice', 'test'
    subject: null,
    chapter: null
  });
  const [selectedTab, setSelectedTab] = useState('new-quest'); // 'new-quest', 'ongoing', 'completed', 'drafts'
  // Pagination state
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10); // UI requirement: show up to 10 recent quests per tab
  const [totalCount, setTotalCount] = useState(0);
  const [nextPageUrl, setNextPageUrl] = useState(null);
  const [previousPageUrl, setPreviousPageUrl] = useState(null);

  // Fetch quests based on current filters and tab
  const fetchQuests = useCallback(async (opts = {}) => {
    const portal = getPortalType();
    const isAuthed = isAuthenticated(portal);
    const isActuallyGuest = localStorage.getItem('is_guest_session') === 'true';

    if (!isAuthed || isActuallyGuest) {
      setQuests([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const params = {};

      // Apply tab-based status filter
      if (selectedTab === 'ongoing') {
        params.status = 'in_progress';
      } else if (selectedTab === 'completed') {
        params.status = 'completed';
      } else if (selectedTab === 'drafts') {
        params.status = 'draft';
      }

      // Apply additional filters
      if (filters.status && !params.status) {
        params.status = filters.status;
      }
      if (filters.mode) {
        params.mode = filters.mode;
      }
      if (filters.subject) {
        params.subject = filters.subject;
      }
      if (filters.chapter) {
        params.chapter = filters.chapter;
      }

      // Pagination
      params.page = opts.page || page || 1;
      params.page_size = pageSize;
      if (opts.fresh) {
        params.fresh = 1;
      }

      const data = await questsAPI.list(params);

      // questsAPI.list now returns paginated object { results, count, next, previous, page, pageSize }
      if (data) {
        const results = Array.isArray(data.results) ? data.results : (Array.isArray(data) ? data : []);

        // Trust server-side status filtering/pagination to keep page counts accurate.
        setQuests(results);
        setTotalCount(data.count || results.length);
        setNextPageUrl(data.next || null);
        setPreviousPageUrl(data.previous || null);
        setPage(data.page || params.page);
      } else {
        setQuests([]);
        setTotalCount(0);
        setNextPageUrl(null);
        setPreviousPageUrl(null);
      }
    } catch (err) {
      console.error('Error fetching quests:', err);
      setError(err.message || 'Failed to fetch quests');
      setQuests([]);
      setTotalCount(0);
      setNextPageUrl(null);
      setPreviousPageUrl(null);
    } finally {
      setLoading(false);
    }
  }, [selectedTab, filters, page, pageSize]);

  // Fetch quests when filters or tab changes
  useEffect(() => {
    // When tab changes, reset to first page
    if (selectedTab !== 'new-quest') {
      setPage(1);
      fetchQuests({ page: 1, fresh: true });
    } else {
      setQuests([]);
      setLoading(false);
      setTotalCount(0);
      setNextPageUrl(null);
      setPreviousPageUrl(null);
      setPage(1);
    }
  }, [selectedTab, filters, fetchQuests]);

  // Page controls
  const nextPage = useCallback(() => {
    if (!nextPageUrl) return;
    const newPage = page + 1;
    setPage(newPage);
    fetchQuests({ page: newPage });
  }, [nextPageUrl, page, fetchQuests]);

  const prevPage = useCallback(() => {
    if (!previousPageUrl) return;
    const newPage = Math.max(1, page - 1);
    setPage(newPage);
    fetchQuests({ page: newPage });
  }, [previousPageUrl, page, fetchQuests]);

  // Generate new quest
  const generateQuest = useCallback(async (questData) => {
    setLoading(true);
    setError(null);

    try {
      const newQuest = await questsAPI.generate(questData);

      // Refresh quest list
      if (selectedTab !== 'new-quest') {
        await fetchQuests({ fresh: true });
      }

      return newQuest;
    } catch (err) {
      console.error('Error generating quest:', err);
      setError(err.message || 'Failed to generate quest');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [selectedTab, fetchQuests]);

  // Get quest by ID
  const getQuest = useCallback(async (questId) => {
    try {
      const quest = await questsAPI.get(questId);
      return quest;
    } catch (err) {
      console.error('Error fetching quest:', err);
      setError(err.message || 'Failed to fetch quest');
      throw err;
    }
  }, []);

  // Update quest
  const updateQuest = useCallback(async (questId, updates) => {
    setLoading(true);
    setError(null);

    try {
      const updated = await questsAPI.update(questId, updates);

      // Update local state
      setQuests(prevQuests =>
        prevQuests.map(q => q.id === questId ? updated : q)
      );

      return updated;
    } catch (err) {
      console.error('Error updating quest:', err);
      setError(err.message || 'Failed to update quest');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Delete quest
  const deleteQuest = useCallback(async (questId) => {
    setLoading(true);
    setError(null);

    try {
      await questsAPI.delete(questId);

      // Update local state
      setQuests(prevQuests => prevQuests.filter(q => String(q.id) !== String(questId)));
      return { deleted: true };
    } catch (err) {
      console.error('Error deleting quest:', err);
      
      // If quest not found, it's already deleted - remove from local state anyway
      if (err.message?.includes('No QuestSet matches') || err.message?.includes('not found') || err.message?.includes('404')) {
        console.log('Quest already deleted on backend, removing from local state');
        setQuests(prevQuests => prevQuests.filter(q => String(q.id) !== String(questId)));
        return { deleted: true, alreadyDeleted: true };
      }
      
      setError(err.message || 'Failed to delete quest');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Record revisit action
  const revisitQuest = useCallback(async (questId, action = 'resumed') => {
    try {
      await questsAPI.revisit(questId, { action });

      // Refresh quest list
      await fetchQuests({ fresh: true });
    } catch (err) {
      console.error('Error recording revisit:', err);
      setError(err.message || 'Failed to record revisit');
    }
  }, [fetchQuests]);

  // Update filters
  const updateFilters = useCallback((newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  }, []);

  // Clear filters
  const clearFilters = useCallback(() => {
    setFilters({
      status: null,
      mode: null,
      subject: null,
      chapter: null
    });
  }, []);

  const value = {
    // State
    quests,
    loading,
    error,
    filters,
    selectedTab,
    page,
    pageSize,
    totalCount,
    nextPageUrl,
    previousPageUrl,

    // Actions
    setSelectedTab,
    fetchQuests,
    generateQuest,
    getQuest,
    updateQuest,
    deleteQuest,
    revisitQuest,
    updateFilters,
    clearFilters
    ,
    // Pagination actions
    setPage,
    nextPage,
    prevPage
  };

  return (
    <QuestsContext.Provider value={value}>
      {children}
    </QuestsContext.Provider>
  );
};
